import { prop, pick } from 'ramda';
import React, { memo, useMemo } from 'react';
import { useGameConfigurationSelector } from '@core/js/store/hooks';
import { CATEGORY_TYPES, CATEGORY_VIEWS } from '@core/js/hooks/games/types';
import { useJackpotsSelector } from '@core/js/store/hooks/useJackpotsSelector';

import * as Types from '@core/js/components/main/GamesGrid/components/GamesCategory/types';
import { GamesGridView } from '@core/js/components/main/GamesGrid/components/GamesGridView';
import { GamesSliderView } from '../GamesSliderView';
import { categorySelector } from '@core/js/components/main/GamesGrid/store/helpers';
import { useDynamicReducerSelector } from '@core/js/components/main/GamesGrid/store';
import { GamesCarouselView } from '@core/js/components/main/GamesGrid/components/GamesCarouselView';
import { GamesWebContentView } from '@core/js/components/main/GamesGrid/components/GamesWebContentView';

import * as helpers from '@core/js/components/main/GamesGrid/components/GamesCategory/helpers';

import style from '@core/js/components/main/GamesGrid/components/GamesCategory/GamesCategory.scss';

export const GamesCategoryBare: React.FC<Types.IGamesCategoryProps> = ({ categoryName, feedName }) => {
    const jackpots = useJackpotsSelector(prop('jackpots'));
    const allGames = useGameConfigurationSelector(prop('games'));
    const category = useDynamicReducerSelector(categorySelector(categoryName));
    const {
        filterSortBy,
        filterLabels,
        favoriteGames,
        filterCategory,
        filterProviders,
        filterCustomFields
    } = useDynamicReducerSelector(pick(['filterSortBy', 'filterProviders', 'filterLabels', 'filterCategory', 'filterCustomFields', 'favoriteGames']));
    const { categoryConfiguration } = category || {};
    const {
        categoryGames,
        categoryView,
        slidesNumber,
        categoryType,
        layoutsNumber,
        categoryWebContent,
        isShownSliderArrows,
        categoryGameGridLayout,
        carouselTileSize,
        carouselGamesNumber,
        carouselGamesNumberInRow
    } = categoryConfiguration || {};

    const { games, categoryLayoutWithGames } = useMemo(() => {
        let filteredGames = [...categoryGames || []];

        if (categoryType === CATEGORY_TYPES.favorites) {
            filteredGames = favoriteGames;
        }

        filteredGames = helpers.filterAndSortGames({
            allGames,
            jackpots,
            categoryView,
            categoryName,
            filterSortBy,
            filterLabels,
            filterCategory,
            filterProviders,
            filterCustomFields,
            categoryGames: filteredGames
        });

        let itemsPerLayout = categoryGameGridLayout?.layoutTiles.length || 0;

        if (categoryView === CATEGORY_VIEWS.carousel) {
            itemsPerLayout = Number(carouselGamesNumberInRow);
        }

        const layoutWithGames = helpers.getCategoryLayoutWithGames(filteredGames, itemsPerLayout);

        return { games: filteredGames, categoryLayoutWithGames: layoutWithGames };
    }, [
        allGames,
        jackpots,
        categoryView,
        categoryType,
        categoryName,
        filterSortBy,
        filterLabels,
        favoriteGames,
        categoryGames,
        filterCategory,
        filterProviders,
        filterCustomFields,
        categoryGameGridLayout,
        carouselGamesNumberInRow
    ]);

    const categoryRowsCount = ((): number => {
        switch (categoryView) {
            case CATEGORY_VIEWS.grid:
                return Number(layoutsNumber);
            case CATEGORY_VIEWS.slider:
                return Number(slidesNumber);
            case CATEGORY_VIEWS.carousel:
                return Number(carouselGamesNumber) <= Number(carouselGamesNumberInRow)
                    ? 1
                    : Math.ceil((Number(carouselGamesNumber) + 1) / Number(carouselGamesNumberInRow));
            default:
                return 0;
        }
    })();

    if (!games?.length) {
        return null;
    }

    return (
        <div className={`${style.container} cms-games-grid-category`}>
            {categoryView === CATEGORY_VIEWS.grid && (
                <GamesGridView
                    feedName={feedName}
                    categoryName={categoryName}
                    allGamesCount={games.length}
                    categoryRowsCount={categoryRowsCount}
                    categoryLayoutWithGames={categoryLayoutWithGames}
                />
            )}
            {categoryView === CATEGORY_VIEWS.slider && (
                <GamesSliderView
                    feedName={feedName}
                    categoryName={categoryName}
                    allGamesCount={games.length}
                    categoryRowsCount={categoryRowsCount}
                    isSliderArrowsShown={isShownSliderArrows}
                    tileGap={categoryGameGridLayout?.tileGap}
                    categoryLayoutWithGames={categoryLayoutWithGames}
                    rows={categoryGameGridLayout?.rows} // Lugano changes
                />
            )}
            {categoryView === CATEGORY_VIEWS.webcontent && (
                <GamesWebContentView
                    feedName={feedName}
                    categoryName={categoryName}
                    categoryWebContent={categoryWebContent}
                />
            )}
            {(categoryView === CATEGORY_VIEWS.carousel || categoryView === CATEGORY_VIEWS.top) && (
                <GamesCarouselView
                    feedName={feedName}
                    categoryName={categoryName}
                    categoryView={categoryView}
                    allGamesCount={games.length}
                    carouselTileSize={carouselTileSize}
                    categoryRowsCount={categoryRowsCount}
                    carouselGamesNumber={carouselGamesNumber}
                    isShownSliderArrows={isShownSliderArrows}
                    categoryLayoutWithGames={categoryLayoutWithGames}
                    carouselGamesNumberInRow={carouselGamesNumberInRow}
                    games={games.slice(0, Number(carouselGamesNumber) + 1)}
                />
            )}
        </div>
    );
};

export const GamesCategory = memo(GamesCategoryBare);
